/**
 * One workflow in Home's Workflows tab: a glyph for how its last run went
 * (red only when it failed), its name, and its next or last run.
 *
 * @module components/home/HomeWorkflowRow
 */
import type { WorkflowSummary } from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CirclePauseIcon,
  CircleSlashIcon,
  LoaderCircleIcon,
} from "lucide-react";
import type { ComponentType } from "react";

import { cn } from "~/lib/utils";
import { ListRow, ListRowDate, ListRowIcon, ListRowTitle } from "../ui/list-row";
import { Tip } from "../ui/tip";
import { workflowRowText, type WorkflowState } from "./homeTabs.logic";

const STATE_ICON: Readonly<Record<WorkflowState, ComponentType<{ className?: string }>>> = {
  failed: CircleAlertIcon,
  running: LoaderCircleIcon,
  waiting: CirclePauseIcon,
  succeeded: CircleCheckIcon,
  cancelled: CircleSlashIcon,
  never: CircleDashedIcon,
};

export function HomeWorkflowRow({
  workflow,
  orgSegment,
  now,
}: {
  readonly workflow: WorkflowSummary;
  readonly orgSegment: string;
  readonly now: number;
}) {
  const { state, tooltip, trailing } = workflowRowText(workflow, now);
  const Icon = STATE_ICON[state];
  return (
    <Tip label={tooltip}>
      <ListRow render={<Link to="/$org/workflows/$workflowId" params={{ org: orgSegment, workflowId: workflow.id }} />}>
        <ListRowIcon className={cn(state === "failed" && "text-destructive")}>
          <Icon className={cn(state === "running" && "animate-spin motion-reduce:animate-none")} />
        </ListRowIcon>
        <ListRowTitle>{workflow.name}</ListRowTitle>
        {trailing ? <ListRowDate>{trailing}</ListRowDate> : null}
      </ListRow>
    </Tip>
  );
}
